


















import { computed, defineComponent, ref, onMounted } from "@vue/composition-api";
import AttachedDeviceModel from "@/models/AttachedDeviceModel";
import { alarm, sensor, app } from "@/store";
import useUserSettings from "@/store/useUserSettings";
import UserSettingsViewModel from "@/models/UserSettingsViewModel";
import { enrollState } from '@/store';
import { enrollUi } from '@/store/enrollUi';

export default defineComponent({
    components: {
        DigitalIOListItem: () => import("./Sensors/DigitalIOListItem.vue"),
        GasValveListItem: () => import("./Sensors/GasValveListItem.vue"),
        CurrentSensorItem: () => import("./Sensors/CurrentSensorListItem.vue"),
    },

    setup() {
        const { getUserSettings, updateUserSettings } = useUserSettings();

        // ✅ Full reactive shape from the start (Vue 2 reactivity)
        const userSettings = ref<UserSettingsViewModel>({
            email: "",
            digitalOutputNickname: "Digital Output",
            gasThreshold: 600,
            bilgeThreshold: 1, // amps; UI range 1..5
            timezone: "Europe/Copenhagen",
            insertedDate: "",
            modifiedDate: "",
        });

        // --- helpers ---
        const getItem = (type: number): AttachedDeviceModel => {
            let model: AttachedDeviceModel = new AttachedDeviceModel();
            if (sensor.devices && sensor.devices.length > 0) {
                const found = sensor.devices.find((v: AttachedDeviceModel) => v.attachedDeviceTypeId === type);
                if (found) return found;
            }
            model.attachedDeviceTypeId = type;
            return model;
        };

        // --- models ---
        const digitalIOModel = computed<AttachedDeviceModel>(() => getItem(16));
        const gasValveModel = computed<AttachedDeviceModel>(() => getItem(15));
        const bilgePumpModel = computed<AttachedDeviceModel>(() => getItem(17));

        const bilgeThreshold = computed<number>(() => {
            return userSettings.value.bilgeThreshold || 1;
        });

        const gasThreshold = computed<number>(() => {
            return userSettings.value.gasThreshold || 600;
        });

        const digitalOutputNickname = computed<string>(() => userSettings.value.digitalOutputNickname || "Digital Output");

        // --- events from children ---
        const handleOnNewNickname = async (model: UserSettingsViewModel) => {
            userSettings.value.digitalOutputNickname = model.digitalOutputNickname || "Digital Output";
            // Optional: persist nickname immediately (uncomment if you want instant save)
            // await updateUserSettings(userSettings.value).catch(err => console.error(err));
        };

        const handleOnNewCurrentThreshold = async (model: UserSettingsViewModel) => {
            // coerce & clamp to UI limits
            const raw = Number(model.bilgeThreshold);
            const clamped = isFinite(raw) ? Math.min(5, Math.max(1, raw)) : 1;
            userSettings.value.bilgeThreshold = clamped;

            try {
                // Convert amps to miliamps for device and send as hex string eg "03E8" for 1000mA
                alarm.setBilgeThreshold((clamped * 1000).toString(16).padStart(4, '0').toUpperCase());

                await updateUserSettings(userSettings.value);
            } catch (err) {
                app.error = "Failed to update bilge pump threshold.";
                console.error("Error updating bilge threshold:", err);
            }
        };

        const panel = computed<number>(() => (app.getControlPanelOpen ? app.getControlPanelIndex : -1));

        function openEnroll(payload: { typeId: number; img: string }) {
            enrollState.startEnrollment(payload.typeId);
            enrollUi.open(payload.img);
        }

        function unpair(payload: { deviceId: string }) {
            sensor.UnpairDevice(payload.deviceId);
        }

        // Load once on mount (Profile-style)
        onMounted(async () => {
            try {
                const value = await getUserSettings();
                userSettings.value = {
                    ...userSettings.value,
                    ...value,
                    // ensure numerics
                    gasThreshold: Number(value?.gasThreshold ?? 600),
                    bilgeThreshold: Number(value?.bilgeThreshold ?? 1),
                };
                if (!isFinite(userSettings.value.bilgeThreshold)) userSettings.value.bilgeThreshold = 1;
            } catch (e) {
                console.error("Failed to load user settings", e);
            }
        });

        return {
            gasValveModel,
            digitalIOModel,
            bilgePumpModel,
            digitalOutputNickname,
            handleOnNewNickname,
            handleOnNewCurrentThreshold,
            panel,
            openEnroll,
            unpair,
            bilgeThreshold,
            gasThreshold,
        };
    },
});
